import { inject, Injectable } from '@angular/core';
import {
    ProjectLineItem,
    ReceiptLine,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import { joinStrings } from '@pf/shared-utility';
import { startCase } from 'lodash-es';
import { IChangeTrackingFacade, IEntity, SafeAny } from '@pf/shared-common';
import { ProjectLineItemFacade } from '@pf/platform-wms/domain';

export type SourceEntityInfo = {
    label: string;
    identifier: string;
};

@Injectable()
export class SourceEntityService {
    private readonly projectLineItemFacade = inject(ProjectLineItemFacade);

    private static readonly displayNames: Partial<
        Record<WmsPlatformEntity, string>
    > = {
        [WmsPlatformEntity.Audit]: 'Cycle Count',
        [WmsPlatformEntity.ProjectLineItem]: 'Work Order',
        [WmsPlatformEntity.PutAway]: 'Put Away',
        [WmsPlatformEntity.ReceiptLine]: 'Receipt',
        [WmsPlatformEntity.Replenishment]: 'Replenishment',
    };

    private getDisplayName(entityType: WmsPlatformEntity): string {
        return (
            SourceEntityService.displayNames[entityType] ??
            startCase(entityType)
        );
    }

    getSourceInfo(
        entityType: WmsPlatformEntity,
        source: IEntity & SafeAny
    ): SourceEntityInfo {
        switch (entityType) {
            case WmsPlatformEntity.ReceiptLine:
                return {
                    label: this.getDisplayName(entityType),
                    identifier: joinStrings(
                        '-',
                        (source as ReceiptLine).inventoryReceipt
                            ?.externalReferenceId,
                        (source as ReceiptLine).lineNumber?.toString()
                    ),
                };
            case WmsPlatformEntity.ProjectLineItem:
                return {
                    label: this.getDisplayName(entityType),
                    identifier: joinStrings(
                        '-',
                        (source as ProjectLineItem).project?.number,
                        (source as ProjectLineItem).lineNumber?.toString()
                    ),
                };
            default:
                return {
                    label: this.getDisplayName(entityType),
                    identifier: source.externalReferenceId || '',
                };
        }
    }

    getChangeTrackingFacade(
        sourceEntity: WmsPlatformEntity,
        source: IEntity
    ): IChangeTrackingFacade<IEntity> | null {
        switch (sourceEntity) {
            case WmsPlatformEntity.ProjectLineItem:
                return this.projectLineItemFacade.changeTrackingFactory(
                    source.id,
                    this.getSourceParentId(sourceEntity, source)
                ) as SafeAny;
            default:
                return null;
        }
    }

    private getSourceParentId(
        sourceEntity: WmsPlatformEntity,
        source: IEntity
    ): string | undefined {
        switch (sourceEntity) {
            case WmsPlatformEntity.ProjectLineItem:
                return (source as ProjectLineItem).projectId;
            default:
                return undefined;
        }
    }
}
